import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import {
    popModal,
    fetchModalData,
    updateModalData,
    saveModalData,
    saveTicketModal,
    submitFilesModal,
    submitAuditNoteFormData,
    resendInvite,
} from "../../../store/actions";

import { views } from "../../../store";
import { FORM_ACTIONS } from "../../../store/views/FormActions";
import {
    SovosBox,
    SovosButton,
    SovosButtonsGroup,
    SovosDrawerHeader,
    SovosTypography,
} from "mosaic-react";

import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";

import styles from "./ModalViewController.module.sass";
import { useTheme } from "mosaic-react/styles";

const ModalViewController = (props) => {
    const {
        id,
        view,
        title,
        data,
        args,
        status,
        errors,
        pop,
        fetchData,
        updateData,
        saveData,
        saveTicket,
        modalType,
        submitFilesModal,
        submitNCAuditNote,
        resendInvite,
        customHeaderButton: CustomHeaderButton,
        customHeaderButtonNames = {},
    } = props;
    const { cancel = undefined, save = undefined } = customHeaderButtonNames || {};
    const { i18n, pushNotification } = useGlobalValue();
    const View = views[view];
    const form = useRef(null);
    const theme = useTheme();
    const [errorState, setErrorState] = useState(errors);
    const [disableSaveButton, setDisableSaveButton] = useState(true);

    const hiddenDiv = useRef(null);
    const hasEnabledSaveButton = useRef(false);

    const scrollToTop = () =>
        hiddenDiv &&
        hiddenDiv.current &&
        hiddenDiv.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });

    const actions = (View.actions || []).map((action, index) => {
        switch (action) {
            case FORM_ACTIONS.SAVE:
                return (
                    <SovosButton
                        key={index}
                        onClick={() => {
                            if (form.current) {
                                form.current.submit();
                                scrollToTop();
                            }
                        }}
                        children={i18n`Save`}
                        variant={"contained"}
                    />
                );
            case FORM_ACTIONS.SUBMIT:
                return (
                    <SovosButton
                        key={index}
                        onClick={() => {
                            if (form.current) {
                                form.current.submit();
                                scrollToTop();
                            }
                        }}
                        children={i18n`Submit`}
                        variant={"contained"}
                    />
                );

            case FORM_ACTIONS.GENERATE:
                return (
                    <SovosButton
                        key={index}
                        onClick={() => {
                            if (form.current) {
                                form.current.submit();
                            }
                        }}
                        children={i18n`Run`}
                        variant={"contained"}
                    />
                );
            default:
                return null;
        }
    });

    useEffect(() => {
        if (View && status === "initial") {
            const queries = (View.queries && View.queries(args || {})) || [];
            const mapper = View.dataMapper || [];

            if (queries.length && mapper.length) {
                fetchData({ id, queries, mapper });
            } else {
                updateData({ id, data });
            }
        }
    }, [id, data, View, args, fetchData, updateData, status]);

    useEffect(() => {
        if (errors?.length) {
            setErrorState(errors);
            setTimeout(() => {
                setDisableSaveButton(false);
            }, 100);
        }
    }, [errors]);

    useEffect(() => {
        if (!hasEnabledSaveButton.current && (status === "success" || (status !== "initial" && status !== "loading" && View))) {
            setDisableSaveButton(false);
            hasEnabledSaveButton.current = true;
        }
    }, [status, View]);

    return (
        <>
            {!CustomHeaderButton && status !== undefined ? (
                <SovosBox sx={{ width: "100%" }}>
                    <SovosDrawerHeader title={i18n`${title}`}>
                        <SovosButtonsGroup align={"right"}>
                            <SovosButton
                                onClick={() => {
                                    pop(id);
                                }}
                                children={cancel ? i18n`${cancel}` : i18n`Cancel`}
                                variant={"text"}
                            />
                            <SovosButton
                                children={save ? i18n`${save}` : i18n`Save`}
                                onClick={() => {
                                    setDisableSaveButton(true);
                                    if (form.current) {
                                        form.current.submit();
                                        scrollToTop();
                                    }
                                }}
                                disabled={disableSaveButton}
                            />
                        </SovosButtonsGroup>
                    </SovosDrawerHeader>
                </SovosBox>
            ) : (
                CustomHeaderButton && <CustomHeaderButton {...props} />
            )}
            <div ref={hiddenDiv} className={styles["hidden-div"]} />
            <main
                className={`${styles["main"]}${modalType === "audit" ? styles["main-chat"] : ""}   `}
            >
                {
                    <div className={styles["errors-container"]}>
                        {(errorState || []).map(({ message }, index) => (
                            <SovosBox
                                key={index}
                                sx={{
                                    backgroundColor: "error.main",
                                    color: "text.contrast",
                                    padding: 1.5,
                                }}
                                className={styles["error"]}
                            >
                                <SovosTypography children={i18n`${message}`} />
                                <SovosButton
                                    color={theme.palette.text.contrast}
                                    variant={"text"}
                                    children={i18n`Close`}
                                    onClick={() => setErrorState([])}
                                />
                            </SovosBox>
                        ))}
                    </div>
                }
                {View && (
                    <View
                        {...(data || {})}
                        ref={form}
                        status={status}
                        onChange={(e) => {
                            updateData({
                                id,
                                data: e.target.value,
                            });
                        }}
                        resendInvite={resendInvite}
                        onSave={() => {
                            switch (view) {
                                case "TicketForm":
                                    saveTicket(id);
                                    break;
                                case "FileSubmissionForm":
                                    submitFilesModal(id);
                                    break;
                                case "AuditNotesForm":
                                case "AuditNoteFindingHistoryForm":
                                    submitNCAuditNote(id, pushNotification);
                                    break;
                                default:
                                    saveData(id, pushNotification);
                                    break;
                            }
                        }}
                        errors={errorState || []}
                    />
                )}
                {modalType === "center" ? (
                    <div className={`${styles["actions-center"]}`}>{actions}</div>
                ) : null}
            </main>
        </>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        args: (state.modalsData[ownProps.id] || {}).args,
        data: (state.modalsData[ownProps.id] || {}).data,
        status: (state.modalsData[ownProps.id] || {}).status,
        errors: (state.modalsData[ownProps.id] || {}).errors,
    };
};

const mapDispatchToProps = {
    pop: popModal,
    fetchData: fetchModalData,
    updateData: updateModalData,
    saveData: saveModalData,
    saveTicket: saveTicketModal,
    submitFilesModal: submitFilesModal,
    submitNCAuditNote: submitAuditNoteFormData,
    resendInvite,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModalViewController);
